/**
 * PERSIST LAB — Code101 chapter 6, where you find out what was on the desk.
 *
 * FOUNDER, 2026-08-14: "ch6 next".
 *
 * ── THE ONE LINE, AS A THING THAT HAPPENS TO YOU ──────────────────────────────────────────────
 * §1: "memory is the desk, storage is the cabinet. Closing an app clears the desk. The cabinet is
 * untouched." A learner can nod at that sentence and still lose an evening's work next month. So
 * the lab makes it happen: type a message, watch the autosave land in the cabinet a few seconds
 * behind your fingers, then pull the power and read the gap between what you wrote and what came
 * back. The gap is the lesson and it has a character count.
 *
 * ── WHY THE UNSAVED PART IS SHOWN, NOT JUST THE SURVIVOR ──────────────────────────────────────
 * Showing only what came back asks the learner to remember what they typed and subtract. They will
 * not, and the interesting quantity is the difference. So the surface shows the recovered text and
 * the lost tail as two visibly different things.
 *
 * ── WHY THE SWITCH IS "WELL-BUILT", NOT "AUTOSAVE ON" ─────────────────────────────────────────
 * §2 does not describe autosave as a feature. It describes it as the difference between software
 * that is well built and software that is not: "Well-built software writes to storage often." The
 * toggle carries his framing, because a learner meeting this in a product will not be shown a
 * setting — they will just lose their work, and the useful thought is "who built this, and how
 * often did they write to storage".
 *
 * ── WHAT IT REFUSES ───────────────────────────────────────────────────────────────────────────
 * Nothing here touches the real device. No localStorage, no IndexedDB, no file. A lab about
 * persistence that quietly persisted would be a lab whose demonstration cannot be repeated — and
 * on a shared phone in a village it would leave a stranger's half-typed message on the screen. The
 * cabinet is a variable in this page and dies with it, which is a small irony stated on the surface
 * rather than hidden.
 *
 * The cache is §5 and is NOT modelled — a third container with a problem of its own. Named on the
 * surface so a learner does not conclude there are only two places a fact can live.
 */

import { useState, useEffect, useRef } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  PROPERTIES, EVENTS, AUTOSAVE_MS, WELL_BUILT, ONE_LINE, SOURCE_1, SOURCE_2, NOT_MODELLED,
  empty, type as typeInto, save, interrupt, reopen,
} from './persist.mjs';

export default function PersistLab() {
  const [s, setS] = useState(empty);
  const [wellBuilt, setWellBuilt] = useState(true);
  const [aftermath, setAftermath] = useState(null);   // { survived, lost, lostChars, event }
  const [saveFlash, setSaveFlash] = useState(false);
  const tick = useRef(0);

  /* The autosave, at his interval. Deliberately slow enough to SEE: the whole point is that the
     cabinet trails the desk, and an instant save would show two panes that always agree. */
  useEffect(() => {
    if (!wellBuilt) return undefined;
    tick.current = setInterval(() => {
      setS((cur) => {
        if (cur.desk === cur.cabinet) return cur;
        setSaveFlash(true);
        setTimeout(() => setSaveFlash(false), 420);
        return save(cur);
      });
    }, AUTOSAVE_MS);
    return () => clearInterval(tick.current);
  }, [wellBuilt]);

  const behind = s.desk.length - s.cabinet.length;

  const fire = (ev) => {
    const r = interrupt(s);
    setS(r.state);
    setAftermath({ ...r, event: ev });
  };

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Persist lab · the desk and the cabinet</div>
      <h2 className="g-h2 lab-h">What survives when the lights go out</h2>
      <p className="g-body">
        Type a message below. A well-built app pushes it into storage every few seconds — you will
        see the cabinet catch up, a little behind your fingers. Then interrupt it, and read the gap.
      </p>
      <p className="g-body g-dim"><Value of={cited(ONE_LINE, SOURCE_1)} /></p>

      <div className="lab-task-head">
        <label className="morse-check">
          <input type="checkbox" checked={wellBuilt} onChange={(e) => { setWellBuilt(e.target.checked); setAftermath(null); }} />
          This app is well built — it writes to storage every {AUTOSAVE_MS / 1000} seconds
        </label>
      </div>

      <div className="per-two">
        {/* ── THE DESK ── */}
        <div className={`per-pane per-desk ${aftermath ? 'per-cleared' : ''}`}>
          <div className="per-head">
            <span className="g-label">{PROPERTIES.memory.name} · {PROPERTIES.memory.picture}</span>
            <span className="per-tag per-vol">volatile</span>
          </div>
          <textarea className="per-text" rows={5} value={s.desk} placeholder="Start typing…"
            onChange={(e) => { setAftermath(null); setS((cur) => typeInto(cur, e.target.value)); }} />
          <div className="lab-readout">
            <div><span className="g-label">On the desk</span><Value of={computed(`${s.desk.length} characters`)} /></div>
            <div><span className="g-label">Speed</span><span className="g-body g-dim">{PROPERTIES.memory.speed}</span></div>
          </div>
        </div>

        {/* ── THE CABINET ── */}
        <div className={`per-pane per-cab ${saveFlash ? 'per-flash' : ''}`}>
          <div className="per-head">
            <span className="g-label">{PROPERTIES.storage.name} · {PROPERTIES.storage.picture}</span>
            <span className="per-tag per-per">persistent</span>
          </div>
          <div className="per-stored">{s.cabinet || <span className="g-body g-dim">nothing has been written yet</span>}</div>
          <div className="lab-readout">
            <div><span className="g-label">In the cabinet</span><Value of={computed(`${s.cabinet.length} characters`)} /></div>
            <div><span className="g-label">Behind the desk by</span>
              <Value of={behind > 0 ? computed(`${behind} characters`) : computed('nothing — caught up')} /></div>
          </div>
          {!wellBuilt && <p className="g-body per-warn">Nothing is being written. The cabinet stays empty until something puts a copy in it.</p>}
        </div>
      </div>

      {/* ── THE INTERRUPTION ── */}
      <div className="lab-task">
        <div className="g-label">Now interrupt it</div>
        <div className="lab-task-head">
          {EVENTS.map((e) => (
            <button key={e.id} className="g-btn" onClick={() => fire(e)} disabled={!s.desk}>{e.label}</button>
          ))}
        </div>

        {aftermath && (
          <div className="per-after">
            <p className="g-body"><b>{aftermath.event.label}.</b> The desk is cleared. The cabinet is untouched.</p>
            <div className="per-recovered">
              <span className="per-kept">{aftermath.survived}</span>
              <span className="per-lost">{aftermath.lost}</span>
            </div>
            <div className="lab-readout">
              <div><span className="g-label">Came back</span><Value of={computed(`${aftermath.survived.length} characters`)} /></div>
              <div><span className="g-label">Gone</span>
                {aftermath.lostChars
                  ? <Value of={computed(`${aftermath.lostChars} characters`)} />
                  : <Value of={absent()} />}</div>
            </div>
            <p className="g-body g-dim per-legend">
              The pale text came back out of the cabinet. The struck-through text was only ever on
              the desk.
            </p>
            {aftermath.event.quote && <p className="g-body g-dim"><Value of={cited(aftermath.event.quote, SOURCE_2)} /></p>}
            <div className="lab-task-head">
              <button className="g-btn g-btn-primary" onClick={() => { setS((c) => reopen(c)); setAftermath(null); }}>
                Reopen the app
              </button>
            </div>
          </div>
        )}

        {/* THE THREE EVENTS ARE ONE EVENT. His parenthetical lists them together, and the machine
            makes no distinction — only the person does. */}
        <p className="g-body g-dim per-same">
          Those four buttons do exactly the same thing, because the machine does not distinguish
          between them. <Value of={cited(PROPERTIES.memory.quote, SOURCE_1)} /> Closing feels
          deliberate and a reboot feels like violence; the desk is equally clear either way.
        </p>
      </div>

      <p className="g-body per-punch"><Value of={cited(WELL_BUILT, SOURCE_2)} /></p>

      <p className="g-body g-dim lab-why">
        Nothing here is written to your device — no file, no browser storage. The cabinet is a
        variable on this page and dies when you leave it, which is a small irony worth saying out
        loud: a lab about persistence that actually persisted could not be run twice, and on a
        shared phone it would leave your message for a stranger.
        <br />
        There is a third place a fact can live and it is not modelled here:
        <Value of={cited(NOT_MODELLED.cache, NOT_MODELLED.source)} /> {NOT_MODELLED.why}
      </p>
    </div>
  );
}
